import React from 'react';
import {
    Button,
    ButtonVariants,
    Text,
    TextAs,
    TextSizes,
    Tooltip,
} from '@clockout-dev/design-elements';
import ReportsFunnelMetricCard from './ReportsFunnelMetricCard';
import EmptyState from '../../common/emptyState/EmptyState';
import RelativeTime from './RelativeTime';
import { isFilterableFunnelCard } from '../funnelStageFilter';
import { FUNNEL_ERROR, FUNNEL_FILTER_COPY } from '../reports.constants';
import styles from './styles/reports-funnel-metrics.module.scss';

/** Error panel copy per `error.reason` (FUNNEL_ERROR); unknown reasons read as a failed load. */
const ERROR_COPY = Object.freeze({
    [FUNNEL_ERROR.LOAD_FAILED]: {
        title: "We couldn't load the enrollment funnel",
        description: 'Please try again.',
    },
    [FUNNEL_ERROR.EMPTY]: {
        title: 'No funnel data yet',
        description: 'The server returned no funnel cards. Try again in a moment.',
    },
});

/**
 * Enrollment funnel section for the Users report (Figma node 5693:16365).
 *
 * Render states:
 *  - loading  → header + label-free skeleton cards (aria-busy)
 *  - error    → header + a section-level error panel with a Retry button —
 *               the grid never silently unmounts; `error.reason` picks the
 *               copy (failed load vs. empty payload)
 *  - loaded   → header (period caption + "opted in" definition tooltip) +
 *               one card per server entry; a card whose count is null renders
 *               "—" with a "not loaded" tooltip while the rest render normally
 *
 * Labels, tones, and definitions all come from the server payload
 * (normalized by `../funnelPayload.js`); this component renders what it is
 * given.
 *
 * Cards are filters (KAN-583): a card the payload marks `filterable` is a
 * button that fires `onStageClick(cardKey)`; `activeStage` marks the selected
 * one; `mismatch` badges the active card when the table total disagrees with
 * its count. The caption gains the click hint when a handler is wired.
 *
 * @param {Object} props
 * @param {Array<{key:string,label:string,count:number|null,deltaLabel?:string,tone?:string,variant?:string,definition?:string|null}> | null} [props.metrics]
 * @param {boolean} [props.loading]
 * @param {{ reason: string } | null} [props.error]  reason: FUNNEL_ERROR value
 * @param {{ type: string, label: string, generatedAt: string|null } | null} [props.range]
 * @param {{ count: number|null, definition: string } | null} [props.optedIn]
 * @param {() => void} [props.onRetry]
 * @param {Array<{key:string,variant?:string}>} [props.placeholders]
 * @param {string|null} [props.activeStage]
 * @param {(stageKey: string) => void} [props.onStageClick]
 * @param {{ stage: string, cardCount: number, tableCount: number } | null} [props.mismatch]
 */
function ReportsFunnelMetrics({
    metrics,
    loading = false,
    error = null,
    range = null,
    optedIn = null,
    onRetry,
    placeholders = [],
    activeStage = null,
    onStageClick,
    mismatch = null,
}) {
    const showError = !loading && Boolean(error);
    const errorCopy = ERROR_COPY[error?.reason] || ERROR_COPY[FUNNEL_ERROR.LOAD_FAILED];

    const items = loading ? placeholders : (metrics || []);
    const COLS = 4;
    const emptySlots = items.length ? (COLS - (items.length % COLS)) % COLS : 0;

    // Plain span wrapper (styled by `.caption`) so the tooltip cue's `Text`
    // below is not a `Text` nested in a `Text`.
    const caption = range ? (
        <span className={styles.caption}>
            {range.label}
            {range.generatedAt ? (
                <>
                    {' · updated '}
                    <RelativeTime value={range.generatedAt} className={styles.caption} />
                </>
            ) : null}
            {' · not affected by the date filters'}
            {typeof onStageClick === 'function' ? ` · ${FUNNEL_FILTER_COPY.CLICK_HINT}` : null}
            {optedIn?.definition ? (
                <>
                    {' · shares are of '}
                    <Tooltip
                        title={
                            optedIn.count != null
                                ? `${optedIn.definition} Currently ${Number(optedIn.count).toLocaleString('en-US')} users.`
                                : optedIn.definition
                        }
                        placement="bottom"
                    >
                        <Text
                            as={TextAs.SPAN}
                            size={TextSizes.EXTRA_SMALL}
                            className={`${styles.caption} ${styles.captionCue}`}
                            tabIndex={0}
                        >
                            opted-in users
                        </Text>
                    </Tooltip>
                </>
            ) : null}
        </span>
    ) : null;

    return (
        <section className={styles.section} aria-label="Enrollment funnel" aria-busy={loading}>
            <div className={styles.header}>
                <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.title}>
                    Enrollment funnel
                </Text>
                {caption}
            </div>
            <div className={styles.shell}>
                {showError ? (
                    <div className={styles.errorPanel}>
                        <EmptyState title={errorCopy.title} description={errorCopy.description} />
                        {onRetry ? (
                            <Button
                                variant={ButtonVariants.DEFAULT}
                                onClick={() => onRetry()}
                            >
                                Retry
                            </Button>
                        ) : null}
                    </div>
                ) : (
                    <div className={styles.grid}>
                        {items.map((item) => (
                            <ReportsFunnelMetricCard
                                key={item.key}
                                label={item.label}
                                count={loading ? null : item.count}
                                deltaLabel={loading ? '' : item.deltaLabel}
                                tone={item.tone}
                                variant={item.variant}
                                definition={loading ? null : item.definition}
                                loading={loading}
                                stageKey={loading ? null : item.key}
                                clickable={!loading && isFilterableFunnelCard(item)}
                                active={!loading && !!activeStage && item.key === activeStage}
                                onClick={onStageClick}
                                mismatch={!loading && mismatch && mismatch.stage === item.key ? mismatch : null}
                            />
                        ))}
                        {Array.from({ length: emptySlots }).map((_, index) => (
                            <div key={`empty-${index}`} className={styles.emptyCell} aria-hidden data-testid="funnel-empty-cell" />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

export default ReportsFunnelMetrics;
